import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../state/auth';
import { useToast } from '../../../state/ToastContext';
import { ProductQuestion } from '../../../domain/types';
import { questionService } from '../../../domain/services/questionService';
import { formatRelativeTimeFull } from '../../../utils/formatters';

interface ProductQuestionsProps {
    productId: string;
    shopId?: string;
    partnerId?: string;
}

export const ProductQuestions: React.FC<ProductQuestionsProps> = ({ productId, shopId, partnerId }) => {
    const { user } = useAuth();
    const { addToast } = useToast();
    const [questions, setQuestions] = useState<ProductQuestion[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [newQuestion, setNewQuestion] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [answeringId, setAnsweringId] = useState<string | null>(null);
    const [answerText, setAnswerText] = useState('');
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [reportingId, setReportingId] = useState<string | null>(null);
    const [reportReason, setReportReason] = useState('');

    const loadQuestions = useCallback(async () => {
        setLoading(true);
        const data = await questionService.getQuestionsByProduct(productId, 20);
        setQuestions(data);
        setLoading(false);
    }, [productId]);

    useEffect(() => {
        loadQuestions();
    }, [loadQuestions]);

    const handleSubmitQuestion = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) {
            addToast('Debes iniciar sesión para preguntar', 'error');
            return;
        }
        if (!newQuestion.trim()) {
            addToast('Escribe tu pregunta', 'error');
            return;
        }

        setSubmitting(true);
        try {
            const newQuestionId = await questionService.createQuestion({
                productId,
                shopId: shopId || '',
                partnerId: partnerId || '',
                userId: user.id,
                userName: user.name,
                userAvatar: user.avatar,
                question: newQuestion.trim(),
                isVerifiedPurchase: false,
                hideFromPublic: false
            });
            
            addToast('Pregunta enviada', 'success');
            setNewQuestion('');
            setShowForm(false);
            loadQuestions();
        } catch (error) {
            addToast('Error al enviar pregunta', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleAnswer = async (questionId: string) => {
        if (!answerText.trim()) return;
        setSubmitting(true);
        try {
            await questionService.answerQuestion(questionId, answerText.trim(), partnerId || '');
            addToast('Respuesta publicada', 'success');
            setAnsweringId(null);
            setAnswerText('');
            loadQuestions();
        } catch (error) {
            addToast('Error al responder', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleVote = async (questionId: string, voteType: 'helpful' | 'not_helpful') => {
        if (!user) {
            addToast('Debes iniciar sesión para votar', 'error');
            return;
        }
        const success = voteType === 'helpful'
            ? await questionService.voteQuestionHelpful(questionId, user.id)
            : await questionService.voteQuestionNotHelpful(questionId, user.id);
        if (success) {
            loadQuestions();
        }
    };

    const handleReport = async (questionId: string) => {
        if (!user) {
            addToast('Debes iniciar sesión para reportar', 'error');
            return;
        }
        if (!reportReason.trim()) {
            addToast('Escribe un motivo para el reporte', 'error');
            return;
        }
        try {
            await questionService.reportQuestion(questionId, user.id, reportReason);
            addToast('Pregunta reportada. Será revisada por un moderador.', 'success');
            setReportingId(null);
            setReportReason('');
        } catch (error) {
            addToast('Error al reportar', 'error');
        }
    };

    const isOwner = user?.id === partnerId;

    return (
        <section className="mt-8 pt-6 border-t border-black/5 dark:border-white/5">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold flex items-center gap-2">
                    <span className="material-symbols-outlined">help</span>
                    Preguntas ({questions.length})
                </h3>
                <button
                    onClick={() => setShowForm(!showForm)}
                    className="text-sm font-medium text-primary hover:underline"
                >
                    {showForm ? 'Cancelar' : 'Preguntar'}
                </button>
            </div>

            {showForm && (
                <form onSubmit={handleSubmitQuestion} className="mb-6 bg-surface-light dark:bg-surface-dark p-4 rounded-xl border border-black/5 dark:border-white/5">
                    <textarea
                        value={newQuestion}
                        onChange={(e) => setNewQuestion(e.target.value)}
                        placeholder="Escribe tu pregunta sobre este producto..."
                        className="w-full p-3 bg-background-light dark:bg-background-dark border border-black/10 dark:border-white/10 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                        rows={3}
                    />
                    <div className="flex justify-end mt-2">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-4 py-2 bg-primary text-white text-sm font-bold rounded-lg hover:bg-primary/90 disabled:opacity-50"
                        >
                            {submitting ? 'Enviando...' : 'Enviar Pregunta'}
                        </button>
                    </div>
                </form>
            )}

            {loading ? (
                <div className="flex justify-center py-8">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
            ) : questions.length === 0 ? (
                <div className="text-center py-8 text-text-secondary dark:text-gray-400">
                    <span className="material-symbols-outlined text-4xl mb-2">chat_bubble</span>
                    <p className="text-sm">Aún no hay preguntas. ¡Sé el primero en preguntar!</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {questions.map((q) => (
                        <div key={q.id} className="bg-surface-light dark:bg-surface-dark rounded-xl border border-black/5 dark:border-white/5 overflow-hidden">
                            <div className="p-4">
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center overflow-hidden shrink-0">
                                        {q.userAvatar ? (
                                            <img src={q.userAvatar} alt={q.userName} className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="text-xs font-bold">{q.userName?.charAt(0).toUpperCase()}</span>
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="font-medium text-sm">{q.userName}</span>
                                            {q.isVerifiedPurchase && (
                                                <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">
                                                    Compra verificada
                                                </span>
                                            )}
                                            <span className="text-xs text-text-secondary dark:text-gray-400">
                                                {formatRelativeTimeFull(q.createdAt)}
                                            </span>
                                        </div>
                                        <p className="text-sm">{q.question}</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4 mt-3 ml-11">
                                    <button
                                        onClick={() => handleVote(q.id, 'helpful')}
                                        className="flex items-center gap-1 text-xs text-text-secondary hover:text-green-600 transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">thumb_up</span>
                                        <span>{q.helpful}</span>
                                    </button>
                                    <button
                                        onClick={() => handleVote(q.id, 'not_helpful')}
                                        className="flex items-center gap-1 text-xs text-text-secondary hover:text-red-600 transition-colors"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">thumb_down</span>
                                        <span>{q.notHelpful}</span>
                                    </button>
                                    <button
                                        onClick={() => setExpandedId(expandedId === q.id ? null : q.id)}
                                        className="text-xs text-primary hover:underline"
                                    >
                                        {expandedId === q.id ? 'Ocultar' : 'Ver más'}
                                    </button>
                                    {!isOwner && user && user.id !== q.userId && (
                                        <button
                                            onClick={() => setReportingId(reportingId === q.id ? null : q.id)}
                                            className="flex items-center gap-1 text-xs text-gray-400 hover:text-orange-500 transition-colors ml-auto"
                                            title="Reportar pregunta inapropiada"
                                        >
                                            <span className="material-symbols-outlined text-[16px]">flag</span>
                                            <span>Reportar</span>
                                        </button>
                                    )}
                                </div>

                                {/* Report Form */}
                                {reportingId === q.id && (
                                    <div className="mt-3 ml-11 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                                        <p className="text-xs text-orange-700 dark:text-orange-300 mb-2 font-medium">
                                            ¿Por qué quieres reportar esta pregunta?
                                        </p>
                                        <textarea
                                            value={reportReason}
                                            onChange={(e) => setReportReason(e.target.value)}
                                            placeholder="Describe el problema..."
                                            className="w-full p-2 bg-white dark:bg-surface-dark border border-orange-200 dark:border-orange-800 rounded-lg resize-none text-sm mb-2"
                                            rows={2}
                                        />
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleReport(q.id)}
                                                className="px-3 py-1.5 bg-orange-500 text-white text-xs font-bold rounded-lg hover:bg-orange-600"
                                            >
                                                Enviar reporte
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setReportingId(null);
                                                    setReportReason('');
                                                }}
                                                className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-xs font-bold rounded-lg"
                                            >
                                                Cancelar
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {q.answer && (
                                    <div className="mt-3 ml-11 bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-800">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="material-symbols-outlined text-green-600 text-[16px]">store</span>
                                            <span className="text-xs font-medium text-green-700 dark:text-green-400">Respuesta del vendedor</span>
                                        </div>
                                        <p className="text-sm text-green-800 dark:text-green-300">{q.answer}</p>
                                        {q.answeredAt && (
                                            <span className="text-xs text-green-600/70 mt-1 block">
                                                {formatRelativeTimeFull(q.answeredAt)}
                                            </span>
                                        )}
                                    </div>
                                )}

                                {isOwner && !q.answer && (
                                    <div className="mt-3 ml-11">
                                        {answeringId === q.id ? (
                                            <div className="space-y-2">
                                                <textarea
                                                    value={answerText}
                                                    onChange={(e) => setAnswerText(e.target.value)}
                                                    placeholder="Escribe tu respuesta..."
                                                    className="w-full p-2 bg-background-light dark:bg-background-dark border border-black/10 dark:border-white/10 rounded-lg resize-none text-sm"
                                                    rows={2}
                                                />
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleAnswer(q.id)}
                                                        disabled={submitting || !answerText.trim()}
                                                        className="px-3 py-1.5 bg-green-600 text-white text-xs font-bold rounded-lg hover:bg-green-700 disabled:opacity-50"
                                                    >
                                                        Responder
                                                    </button>
                                                    <button
                                                        onClick={() => setAnsweringId(null)}
                                                        className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-xs font-bold rounded-lg"
                                                    >
                                                        Cancelar
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => setAnsweringId(q.id)}
                                                className="text-xs text-green-600 hover:underline font-medium"
                                            >
                                                Responder esta pregunta
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                            {expandedId === q.id && (
                                <div className="px-4 pb-4 pt-0">
                                    <div className="h-px bg-black/5 dark:bg-white/5 mb-3"></div>
                                    <p className="text-xs text-text-secondary">
                                        Esta pregunta ha sido útil para {q.helpful + q.notHelpful} personas.
                                    </p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
};
